import { Helmet } from 'react-helmet-async';
import { useBusinessSettings } from '../BusinessSettingsContext';

// Helper to get rental terminology based on industry types
function getRentalTerminology(industryTypes) {
  if (!industryTypes || industryTypes.length === 0) return 'rentals';

  // Priority order for terminology
  if (industryTypes.includes('rv')) return 'RV rentals';
  if (industryTypes.includes('travel_trailer')) return 'travel trailer rentals';
  if (industryTypes.includes('dumpster')) return 'dumpster rentals';
  if (industryTypes.includes('portable_restroom')) return 'portable restroom rentals';
  if (industryTypes.includes('boat')) return 'boat rentals';
  if (industryTypes.includes('equipment')) return 'equipment rentals';

  return 'rentals';
}

/**
 * SEO Component - Manages meta tags for each page
 *
 * Usage:
 * <SEO
 *   title="Page Title"
 *   description="Page description for search results"
 *   image="/path/to/image.jpg"
 *   url="/page-path"
 * />
 */
function SEO({
  title,
  description,
  image,
  url,
  type = 'website',
  noIndex = false,
  children
}) {
  const { settings } = useBusinessSettings();

  // Build full title with business name
  const businessName = settings.business_name || 'RentMeHQ';
  const fullTitle = title ? `${title} | ${businessName}` : businessName;

  // Get industry-specific terminology
  const rentalTerm = getRentalTerminology(settings.industry_types);

  // Use meta_description from settings, fallback to tagline-based description
  const defaultDescription = settings.meta_description
    ? settings.meta_description
    : settings.tagline
      ? `${settings.tagline} - ${businessName} offers quality ${rentalTerm} for your next adventure.`
      : `${businessName} offers quality ${rentalTerm} for your next adventure. Browse our selection and book today!`;

  const metaDescription = description || defaultDescription;

  // Build canonical URL
  const siteUrl = window.location.origin;
  const canonicalUrl = url ? `${siteUrl}${url}` : window.location.href;

  // Use og_image_url first, fallback to logo, then default
  const getImageUrl = (imgUrl) => {
    if (!imgUrl) return null;
    return imgUrl.startsWith('http') ? imgUrl : `${siteUrl}${imgUrl}`;
  };

  // Fallback chain: per-page image → tenant og_image_url → tenant logo
  // → nothing. We intentionally don't ship a static default; social
  // platforms degrade to text-only previews, which beats showing a
  // RentMeHQ marketing pitch on tenant booking links.
  const metaImage = image
    ? getImageUrl(image)
    : settings.og_image_url
      ? getImageUrl(settings.og_image_url)
      : settings.logo_url
        ? getImageUrl(settings.logo_url)
        : null;

  // Favicon from settings
  const faviconUrl = settings.favicon_url
    ? getImageUrl(settings.favicon_url)
    : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />

      {/* Favicon from settings (if set) */}
      {faviconUrl && <link rel="icon" href={faviconUrl} />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      {metaImage && <meta property="og:image" content={metaImage} />}
      <meta property="og:site_name" content={businessName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      {metaImage && <meta name="twitter:image" content={metaImage} />}

      {/* Additional tags passed as children */}
      {children}
    </Helmet>
  );
}

/**
 * Product SEO - For trailer detail pages with structured data
 */
function ProductSEO({ trailer, url }) {
  const { settings } = useBusinessSettings();
  const businessName = settings.business_name || 'RentMeHQ';
  const siteUrl = window.location.origin;

  const title = `${trailer.name} - Rent for $${trailer.price_per_night}/night`;
  const description = trailer.description
    ? trailer.description.substring(0, 155) + '...'
    : `Rent the ${trailer.name} - sleeps ${trailer.sleeps}, ${trailer.length}. Perfect for your next camping adventure!`;

  const image = trailer.images && trailer.images.length > 0
    ? trailer.images[0]
    : null;

  // Product structured data (JSON-LD)
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: trailer.name,
    description: trailer.description || description,
    image: trailer.images || [],
    brand: {
      '@type': 'Brand',
      name: trailer.brand || businessName
    },
    offers: {
      '@type': 'Offer',
      url: `${siteUrl}${url}`,
      priceCurrency: 'USD',
      price: trailer.price_per_night,
      priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      availability: trailer.status === 'available'
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: businessName
      }
    }
  };

  return (
    <SEO
      title={title}
      description={description}
      image={image}
      url={url}
      type="product"
    >
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </SEO>
  );
}

/**
 * LocalBusiness structured data for homepage
 */
function LocalBusinessSEO() {
  const { settings } = useBusinessSettings();
  const siteUrl = window.location.origin;

  // Get industry-specific terminology for description
  const rentalTerm = getRentalTerminology(settings.industry_types);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': siteUrl,
    name: settings.business_name || 'RentMeHQ',
    description: settings.meta_description || settings.tagline || `Quality ${rentalTerm} for your next adventure`,
    url: siteUrl,
    telephone: settings.contact_phone || undefined,
    email: settings.contact_email || undefined,
    address: settings.contact_address ? {
      '@type': 'PostalAddress',
      streetAddress: settings.contact_address
    } : undefined,
    image: settings.og_image_url
      ? `${siteUrl}${settings.og_image_url}`
      : settings.logo_url
        ? `${siteUrl}${settings.logo_url}`
        : undefined,
    sameAs: [
      settings.facebook_url,
      settings.instagram_url,
      settings.tiktok_url,
      settings.youtube_url,
      settings.twitter_url,
      settings.linkedin_url,
    ].filter(Boolean)
  };

  // Remove undefined values
  Object.keys(structuredData).forEach(key => {
    if (structuredData[key] === undefined) {
      delete structuredData[key];
    }
  });

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

/**
 * Breadcrumb structured data
 */
function BreadcrumbSEO({ items }) {
  const siteUrl = window.location.origin;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? `${siteUrl}${item.url}` : undefined
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

export { SEO, ProductSEO, LocalBusinessSEO, BreadcrumbSEO };
export default SEO;
